import { ITEMS, type ItemDef, type SkillDef } from '@nine/shared';
import type { ActionIcon } from '../icons/action/kind';
import { icon, type IconSize } from '../icons/registry';
import { canvas, ctx2d } from './pixel';
import { iconSizeV2, skillIconV2 } from '../icons2/index';
import '../icons2/icons2.css';

// C0-ICON clone: the icon size in use (16, 24, 32 or 48) is set on <html> so the CSS can pick a whole-number scale.
document.documentElement.dataset.isize = String(iconSizeV2());

/**
 * Pixel-art icons for the HUD and windows. The pictures live in `src/icons/` (one folder per kind,
 * collected by `icons/registry.ts`); these are the shortcuts the UI calls. Every icon is 32×32 art
 * pixels with a 16×16 version for small slots, drawn once per size and kept: put one in the page
 * with `iconEl`, shown with `image-rendering: pixelated` at a whole-number scale.
 */

export { RARITY_COLORS } from '../icons/item/kind';
export type { ActionIcon } from '../icons/action/kind';
export type { IconKind, IconSize } from '../icons/registry';
export { icon } from '../icons/registry';

/** A fresh element showing an icon (one canvas element can only sit in one place in the page). */
export function iconEl(src: HTMLCanvasElement, cls = 'icon'): HTMLCanvasElement {
  const c = canvas(src.width, src.height);
  c.className = cls;
  ctx2d(c).drawImage(src, 0, 0);
  return c;
}

/** A move (กระบวนท่า), with its kind mark (攻 破 療 怒). */
export const skillIcon = (s: SkillDef, _size: IconSize = 32): HTMLCanvasElement => skillIconV2(s, iconSizeV2());

/** The icon id of an item: its own, its base's for a rolled item, or one drawn from its look. */
export function itemIconId(it: ItemDef): string {
  if (ITEMS[it.id]) return it.id;
  const base = Object.values(ITEMS).find((b) => b.name === it.name && b.visual === it.visual);
  return base?.id ?? `@${it.visual}:${it.slot}:${it.rarity}`;
}

/** A piece of equipment, on a tile of its rarity. */
export const itemIcon = (it: ItemDef, size: IconSize = 32): HTMLCanvasElement =>
  icon('item', itemIconId(it), size);

/** A general action on the hotbar's top row. */
export const actionIcon = (kind: ActionIcon, size: IconSize = 32): HTMLCanvasElement =>
  icon('action', kind, size);

/** A martial art's emblem (วิชายุทธ์). */
export const artIcon = (artId: string, size: IconSize = 32): HTMLCanvasElement => icon('art', artId, size);

/** A neigong manual (กำลังภายใน), its tier shown as studs. */
export const neigongIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('neigong', id, size);

/** A qinggong (วิชาตัวเบา). */
export const qinggongIcon = (id: string, size: IconSize = 32): HTMLCanvasElement =>
  icon('qinggong', id, size);

/**
 * A status, buff or debuff (16 by default, for status rows). Also `stun`, `meditation`,
 * `training-spot` and `group-dance`.
 */
export const statusIcon = (id: string, size: IconSize = 16): HTMLCanvasElement => icon('status', id, size);

/** A good: material, seed, food or medicine. */
export const goodIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('good', id, size);

/** A school's crest. */
export const schoolIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('school', id, size);

/** A school badge rank, 1 (iron) to 4 (gold). */
export const badgeIcon = (tier: number, size: IconSize = 32): HTMLCanvasElement =>
  icon('badge', String(tier), size);

export const professionIcon = (id: string, size: IconSize = 32): HTMLCanvasElement =>
  icon('profession', id, size);
export const mountIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('mount', id, size);
export const titleIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('title', id, size);
/** A training-spot kind (waterfall, cave, peak, bamboo, hotSpring, shrine, shore). */
export const spotIcon = (kind: string, size: IconSize = 32): HTMLCanvasElement => icon('spot', kind, size);
/** `silver`, `jade` or `cultivation`. */
export const currencyIcon = (id: string, size: IconSize = 32): HTMLCanvasElement =>
  icon('currency', id, size);
export const elementIcon = (id: string, size: IconSize = 32): HTMLCanvasElement => icon('element', id, size);
